import { motion } from "framer-motion"
import {ChevronDown} from 'lucide-react'
import { useState } from "react"
import { useLang } from "@/context/LanguageContext"

export const faqs = [
  {
    qFr: "Clips'On abîme-t-il le tissu ?",
    qEn: "Does Clips'On damage the fabric?",
    aFr: "Sur les tissus à mailles et matières souples (jersey, tricot, tissés lâches, extensibles), le pic écarte les fibres et se glisse entre elles, sans faire de trou. Sur les tissus tissés serrés comme le 100 % coton, il traverse la matière et laisse un petit trou, comme tout bouton à pic. Faites toujours un essai sur une zone non visible (ourlet, couture, revers) avant la pose définitive.",
    aEn: "On knitted and soft fabrics (jersey, knitwear, loosely woven or stretch fabrics), the pin parts the fibres and slides between them without making a hole. On tightly woven fabrics such as 100% cotton, it goes through the material and leaves a small hole, like any prong-style button. Always test on a hidden area (hem, seam, cuff) before final placement.",
  },
  {
    qFr: "Supporte-t-il les lavages et le repassage ?",
    qEn: "Does it withstand washing and ironing?",
    aFr: "Oui. En Nylon 6/6, il résiste aux lavages domestiques et professionnels et au fer.",
    aEn: "Yes. Made of Nylon 6/6, it withstands domestic and professional washing as well as ironing.",
  },
  {
    qFr: "Peut-on changer la couleur ou réutiliser un bouton ?",
    qEn: "Can you change the colour or reuse a button?",
    aFr: "La capsule de couleur se clipse et se déclipse : avec plusieurs boutons de même taille, vous pouvez intervertir les capsules autant de fois que vous voulez. Le pic, lui, n'est pas réutilisable : pour retirer complètement un bouton, on coupe le pic, qui est alors perdu (pas de pics de rechange à la vente pour l'instant). L'embase peut aussi être cousue, capsule clipsée par-dessus — la tenue à la traction dépend alors de la couture.",
    aEn: "The colour cap clips on and off: with several buttons of the same size, you can swap the caps as many times as you like. The pin itself is not reusable: to fully remove a button, the pin is cut and lost (no replacement pins for sale at this time). The base can also be sewn on, with the cap clipped over it — in that case, pull resistance depends on the stitching.",
  },
  {
    qFr: "Quelles tailles et combien de couleurs ?",
    qEn: "What sizes and how many colours?",
    aFr: "Deux tailles, 18 mm et 20 mm. 28 couleurs au catalogue (14 disponibles actuellement).",
    aEn: "Two sizes, 18 mm and 20 mm. 28 colours in the catalogue (14 currently available).",
  },
  {
    qFr: "Clips'On convient-il aux vêtements d'enfants ?",
    qEn: "Is Clips'On suitable for children's clothing?",
    aFr: "Clips'On déconseille l'usage sur les vêtements d'enfants : la capsule amovible peut se détacher et présenter un risque (ingestion, étouffement). Mention légale : produit non adapté aux enfants de moins de 36 mois.",
    aEn: "Clips'On advises against use on children's clothing: the removable cap can detach and present a risk (ingestion, choking). Legal notice: product not suitable for children under 36 months.",
  },
  {
    qFr: "Frais et provenance de livraison ?",
    qEn: "Shipping costs and origin?",
    aFr: "Suisse : expédiée depuis la Suisse, dès 1,50 €. France : expédiée depuis la France, dès 2,90 €. Autres pays européens : dès 5,90 €. Détails à la commande.",
    aEn: "Switzerland: shipped from Switzerland, from €1.50. France: shipped from France, from €2.90. Other European countries: from €5.90. Details at checkout.",
  },
]

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="border-b"
      style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
      >
        <span className="pr-4" style={{ color: "#82391F", fontSize: "17px" }}>
          {q}
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0"
        >
          <ChevronDown size={18} style={{ color: "rgba(225, 89, 63, 0.4)" }} />
        </motion.div>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="overflow-hidden"
      >
        <p className="leading-relaxed pb-6 pl-0" style={{ color: "rgba(13, 12, 11, 0.5)", fontSize: "17px" }}>
          {a}
        </p>
      </motion.div>
    </motion.div>
  )
}

export default function FaqSection() {
  const { lang, t } = useLang()

  return (
    <section id="faq" className="relative py-32" style={{ backgroundColor: "#F8F2EB" }}>
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: "rgba(225, 89, 63, 0.6)" }}>
            {t("Questions fréquentes", "Frequently asked questions")}
          </p>
          <h2 className="text-4xl md:text-5xl mb-6" style={{ color: "#82391F", letterSpacing: "4px" }}>
            FAQ
          </h2>
          <div className="w-16 h-px mx-auto" style={{ backgroundColor: "rgba(225, 89, 63, 0.35)" }} />
        </motion.div>

        {/* FAQ items */}
        <div className="border-t" style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}>
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              q={lang === "fr" ? faq.qFr : faq.qEn}
              a={lang === "fr" ? faq.aFr : faq.aEn}
              index={i}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
